import { parametric, pathSubscription, subscription } from 'nike-re-framejs';
import { orderedIndexToIdsList } from '../indexingUtils';
import { lang } from '../../utils';
import { List, Map, Set } from 'immutable';
import { MatchStatus } from '../../dto';
import { COMBINABLE_BETS } from '../../components/detail/MarketCategories';
import { hasCombinableLiveMarket, hasCombinablePrematchBet } from '../../../combinable-bets/betExclusionValidations';
import { marketCollapse } from '../../../../common/constants';

export const detailMatchFinished$$ = parametric((sportSlug, matchId) =>
    subscription(
        status => status === MatchStatus.FINISHED,
        pathSubscription(['turboLive', 'overview', sportSlug, 'matches', matchId, 'timer', 'status'])
    )
);
export const detailBet$$ = (betId: string) => pathSubscription(['turboLive', 'detail', 'bets', betId]);

export const detailBetIdsForMarket$$ = parametric(marketId =>
    subscription(
        (betIdsOrderedIndex, combinableBetIdsOrderedIndex, market, marketCategory) => {
            if (marketCategory === COMBINABLE_BETS) {
                const combinableBetsFilteredIds = combinableBetIdsOrderedIndex?.map(bet => bet.id);
                return (
                    market?.get('betIds')?.filter(bet => combinableBetsFilteredIds?.contains(bet)) ||
                    orderedIndexToIdsList(combinableBetIdsOrderedIndex)
                );
            } else {
                return market?.get('betIds') || orderedIndexToIdsList(betIdsOrderedIndex);
            }
        },
        pathSubscription(['turboLive', 'detail', 'betIdsByMarket', marketId], List()),
        combinableBetsByMarket$$(marketId) || List(),
        detailMarket$$(marketId),
        selectedMarketCategoryId$
    )
);

export const combinableBetsByMarket$$ = marketId =>
    pathSubscription(['turboLive', 'detail', 'combinableBetIdsByMarket', marketId]);

export const prematchCombBetIdsForMarket$$ = parametric((marketId, source) =>
    subscription(
        (marketCategoryId, combinableBetsFiltered) => {
            if (source === 'detail' && marketCategoryId === COMBINABLE_BETS && combinableBetsFiltered) {
                return combinableBetsFiltered?.map(obj => obj.id);
            }
            return null;
        },
        selectedMarketCategoryId$,
        combinableBetsByMarket$$(marketId)
    )
);

function groupBetsByAdditionalLabel(bets: Map<string, any>, betIds: List<string>, detailAttribute = 'detail') {
    return betIds
        ?.groupBy(betId => bets?.getIn([betId, detailAttribute, 'additionalLabel', lang]) || '')
        .sortBy(betIdsInGroup => bets?.getIn([betIdsInGroup.first(), 'order']));
}

export const detailBetIdsForMarketGrouped$$ = parametric(marketId =>
    subscription(
        (bets: Map<string, any>, betIds: List<string>) => groupBetsByAdditionalLabel(bets, betIds),
        pathSubscription(['turboLive', 'detail', 'bets']),
        detailBetIdsForMarket$$(marketId)
    )
);

export const detailBetIdsForMarketGroupedMobile$$ = parametric(marketId =>
    subscription(
        (bets: Map<string, any>, betIds: List<string>) => groupBetsByAdditionalLabel(bets, betIds, 'detailMobile'),
        pathSubscription(['turboLive', 'detail', 'bets']),
        detailBetIdsForMarket$$(marketId)
    )
);

export const selectionsArrangementForMarket$$ = marketId =>
    pathSubscription(['turboLive', 'detail', 'markets', marketId, 'selectionsArrangement']);

const betsForMarkets$ = parametric(marketCategoryId =>
    subscription(
        ([betIdsByMarket, combinableBetIdsByMarket]) => {
            return marketCategoryId === COMBINABLE_BETS && combinableBetIdsByMarket !== null
                ? combinableBetIdsByMarket
                : betIdsByMarket;
        },
        [
            pathSubscription(['turboLive', 'detail', 'betIdsByMarket']),
            pathSubscription(['turboLive', 'detail', 'combinableBetIdsByMarket'])
        ]
    )
);

export const detailMarketIds$$ = parametric(marketCategoryId =>
    subscription(
        ([marketIdsByOrder, betIdsByMarket, categoryMarketIdSet]) => {
            return orderedIndexToIdsList(marketIdsByOrder)
                ?.filter(marketId => betIdsByMarket?.has(marketId))
                ?.filter(
                    marketId =>
                        marketCategoryId == null ||
                        marketCategoryId === COMBINABLE_BETS ||
                        categoryMarketIdSet.has(marketId)
                );
        },
        [
            pathSubscription(['turboLive', 'detail', 'marketIdsByOrder']),
            betsForMarkets$(marketCategoryId),
            marketCategoryMarketIdSet$(marketCategoryId)
        ]
    )
);

export const detailBetsEmpty$ = subscription(
    marketIdsByOrder => (marketIdsByOrder || List()).isEmpty(),
    pathSubscription(['turboLive', 'detail', 'marketIdsByOrder'])
);

export const marketCategoryMarketIdSet$ = parametric(marketCategoryId =>
    subscription(
        marketIdsList => marketIdsList?.toSet() || Set(),
        pathSubscription(['turboLive', 'detail', 'marketCategories', marketCategoryId, 'marketIds'], List())
    )
);

export const marketsCount$ = subscription(
    markets => markets?.size || 0,
    pathSubscription(['turboLive', 'detail', 'markets'])
);

export const combinableMarketsCount$ = subscription(markets => {
    const marketsWithBets = markets?.filter(market => !market.isEmpty());
    return marketsWithBets?.size || 0;
}, pathSubscription(['turboLive', 'detail', 'combinableBetIdsByMarket']));

export const shouldCombinableMarketRender$ = marketId =>
    pathSubscription(['turboLive', 'detail', 'combinableBetIdsByMarket', marketId]);

export const hasCombinableMarkets$ = subscription(
    ([markets, allowCombinations]) => {
        return hasCombinableLiveMarket(markets, allowCombinations);
    },
    [
        pathSubscription(['turboLive', 'detail', 'markets']),
        pathSubscription(['turboLive', 'detail', 'tournamentAllowMarketCombinations'])
    ]
);

export const hasCombinableBets$ = subscription(bets => {
    return hasCombinablePrematchBet(bets);
}, pathSubscription(['turboLive', 'detail', 'bets']));

export const detailMarket$$ = marketId => pathSubscription(['turboLive', 'detail', 'markets', marketId]);

const detailMarketCollapse$$ = marketId =>
    pathSubscription(['turboLive', 'detail', 'markets', marketId, 'collapse'], marketCollapse.NONE);

export const marketCollapseAll$ = parametric((marketId: string) =>
    subscription(collapse => collapse === marketCollapse.ALL, detailMarketCollapse$$(marketId))
);

export const marketCollapseLabel$ = parametric((marketId: string) =>
    subscription(collapse => collapse === marketCollapse.LABEL, detailMarketCollapse$$(marketId))
);

export const detailMarketCategoryIds$ = subscription(
    orderedIndexToIdsList,
    pathSubscription(['turboLive', 'detail', 'marketCategoryIdsByOrder'])
);

export const detailMarketCategory$$ = marketCategoryId =>
    pathSubscription(['turboLive', 'detail', 'marketCategories', marketCategoryId]);

export const selectedMarketCategoryId$ = pathSubscription(['turboLive', 'detail', 'selectedMarketCategoryId']);

// FIXME taketo prehladavanie DB by sme nemali mat v nike-ui
//magic subs - check turbolive, portal, mobil - data should be somewhere ... do not ask...
export const detailMbmIds$ = parametric(matchId =>
    subscription(
        db =>
            db.getIn(['n1', 'live', matchId, 'mbmIds']) ||
            db
                .getIn(['nikeone', 'detail', 'mbm'])
                ?.keySeq()
                ?.sort()
                ?.reverse()
    )
);
export const hasMbm$ = parametric(matchId => subscription(mbmIds => mbmIds && !mbmIds?.isEmpty(), getMbmIds$(matchId)));

export const isMbmLoaded$$ = pathSubscription(['turboLive', 'detail', 'mbmLoaded'], false);

const getMbmIds$ = parametric(matchId =>
    subscription(
        db =>
            db.getIn(['turboLive', 'detail', 'mbmIdsByOrder']) ||
            db.getIn(['n1', 'live', matchId, 'mbmIds']) ||
            db.getIn(['nikeone', 'detail', 'mbm'])
    )
);

const getMbms$ = parametric(matchId =>
    subscription(
        db =>
            db.getIn(['turboLive', 'detail', 'mbm']) ||
            db.getIn(['n1', 'live', matchId, 'mbm']) ||
            db.getIn(['nikeone', 'detail', 'mbm'])
    )
);

export const hasMbmPeriods$ = subscription(db =>
    db
        .getIn(['turboLive', 'detail', 'mbmIdsByOrder'])
        ?.keySeq()
        .toArray()
        .filter(item => item)
        .sort()
);

export const detailMbmById$ = parametric((matchId, mbmId) =>
    subscription(
        db =>
            db.getIn(['turboLive', 'detail', 'mbm', mbmId]) ||
            db.getIn(['n1', 'live', matchId, 'mbm', mbmId]) ||
            db.getIn(['nikeone', 'detail', 'mbm', mbmId])
    )
);

export const getRainDelay$ = parametric(matchId =>
    subscription(mbms => mbms?.find(mbm => mbm.get('type') === 'RAIN_DELAY'), getMbms$(matchId))
);

export const getFirstMbmByPeriodOrder$ = parametric((matchId, periodOrder) =>
    subscription(([mbms, firstMbm]) => mbms?.get(firstMbm?.last()?.id), [
        pathSubscription(['turboLive', 'detail', 'mbm']),
        detailMbmsByPeriod$(matchId, periodOrder)
    ])
);

export const detailMbmsByPeriod$ = parametric((matchId, periodOrder) =>
    subscription(db => db.getIn(['turboLive', 'detail', 'mbmIdsByOrder', periodOrder])?.reverse())
);
export const mbmMatch$ = parametric((matchId, sportCode) =>
    subscription(
        db =>
            db.getIn(['turboLive', 'overview', sportCode, 'matches', matchId]) ||
            db.getIn(['n1', 'live', matchId, 'match']) ||
            db.getIn(['nikeone', 'live', matchId, 'match']) ||
            db.getIn(['turboLive', 'overview', 'matches', matchId]) ||
            db.getIn(['infocentrum', 'detail', 'match']) ||
            db.getIn(['nikeone', 'detail', 'data', 'sportEvent'])
    )
);
